import { IInvoice } from '../interfaces';

/**
 * Stripe sends the customer back to the invoice page whether they paid or
 * cancelled, and the invoice only turns paid once Stripe's webhook reaches the
 * backend. A note left before the redirect tells this page to wait for that
 * webhook instead of offering the approve buttons again straight away.
 */
const NOTE_PREFIX = 'stripe-return:';
const NOTE_TTL_MS = 60 * 60 * 1000;

type NoteStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const sessionNoteStorage = (): NoteStorage | null => {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

export function noteStripeRedirect(
    invoiceUuid: string,
    now = Date.now(),
    storage: NoteStorage | null = sessionNoteStorage()
): void {
    try {
        storage?.setItem(NOTE_PREFIX + invoiceUuid, String(now));
    } catch {
        // Without the note the customer just sees the approve screen on return.
    }
}

export function hasStripeReturnNote(
    invoiceUuid: string,
    now = Date.now(),
    storage: NoteStorage | null = sessionNoteStorage()
): boolean {
    try {
        const notedAt = Number(storage?.getItem(NOTE_PREFIX + invoiceUuid));
        return notedAt > 0 && now - notedAt < NOTE_TTL_MS;
    } catch {
        return false;
    }
}

export function clearStripeReturnNote(
    invoiceUuid: string,
    storage: NoteStorage | null = sessionNoteStorage()
): void {
    try {
        storage?.removeItem(NOTE_PREFIX + invoiceUuid);
    } catch {
        // A note that cannot be removed goes stale on its own.
    }
}

type WaitOptions = {
    sleep?: (ms: number) => Promise<void>;
    signal?: AbortSignal;
};

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 7;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Re-reads the invoice every two seconds for about fifteen, until it is paid. */
export async function waitForPayment(
    fetchInvoice: () => Promise<IInvoice>,
    { sleep = delay, signal }: WaitOptions = {}
): Promise<IInvoice | null> {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        await sleep(POLL_INTERVAL_MS);
        if (signal?.aborted) return null;
        try {
            const invoice = await fetchInvoice();
            if (signal?.aborted) return null;
            if (invoice.is_signed) return invoice;
        } catch {
            // The next attempt may well succeed.
        }
    }
    return null;
}
